import type { PolicyDocument } from './types';

const COMPANY = 'Zykes Technologies LLP';
const PRODUCT = 'GoGrabJob';
const CONTACT = 'support@gograbjob.com';
const EFFECTIVE = '1 June 2025';
const UPDATED = '8 June 2026';

export const privacyPolicy: PolicyDocument = {
  id: 'privacy',
  title: 'Privacy Policy',
  eyebrow: 'Legal · Privacy',
  effectiveDate: EFFECTIVE,
  lastUpdated: UPDATED,
  summary:
    'We collect only what we need to run your AI job search workspace — match roles, tailor your resume, score ATS fit, track applications, and prep for interviews. We do not sell your resume to recruiters, and you can export or delete your data at any time.',
  sections: [
    {
      title: '1. Who we are',
      paragraphs: [
        `${PRODUCT} is operated by ${COMPANY}, registered in Bengaluru, India. This Privacy Policy explains how we collect, use, store, and delete personal information when you use our website, mobile apps, and AI job search services.`,
        'By creating an account or using our services, you agree to this policy. If you do not agree, please do not use GoGrabJob.',
      ],
    },
    {
      title: '2. Information we collect',
      subsections: [
        {
          title: '2.1 Information you provide',
          list: [
            'Account details: name, email address, phone number (optional), and profile photo (optional).',
            'Career content: resumes, cover letters, job descriptions you paste or upload, and AI-generated outputs.',
            'Preferences: job targets, language settings, and notification choices.',
            'Support messages and feedback you send us.',
          ],
        },
        {
          title: '2.2 Information collected automatically',
          list: [
            'Device and browser data: IP address, device type, operating system, and app version.',
            'Usage data: pages viewed, features used, optimization counts, and error logs.',
            'Cookies and similar technologies — see our Cookie Policy for details.',
          ],
        },
        {
          title: '2.4 Communications and support',
          list: [
            'Email address and message content when you contact support.',
            'In-app feedback and bug reports you submit voluntarily.',
            'Optional phone number if you add it for account recovery or notifications.',
          ],
        },
        {
          title: '2.5 Payment information',
          paragraphs: [
            'Paid plans are processed by our payment partners (e.g. Cashfree, Razorpay). We receive transaction IDs, plan type, and billing status — not full card numbers, which are handled by the processor.',
          ],
        },
      ],
    },
    {
      title: '3. AI and automated processing',
      paragraphs: [
        'GoGrabJob uses AI models to analyse resumes and job descriptions, generate tailored content, and produce ATS compatibility scores. Processing is triggered by your actions — we do not make automated hiring decisions on your behalf.',
      ],
      list: [
        'Inputs: resume text, job descriptions, and preferences you provide.',
        'Outputs: optimised resume drafts, scores, cover letters, and interview prep suggestions.',
        'Your content is sent to AI subprocessors only to deliver the feature you requested.',
        'We do not use your resume to train public foundation models without your explicit consent.',
      ],
    },
    {
      title: '4. How we use your information',
      list: [
        'Deliver core services: AI resume optimization, ATS scoring, cover letter generation, and application tracking.',
        'Personalise your workspace and remember your preferences.',
        'Process subscriptions and send billing receipts.',
        'Send service emails (account alerts, product updates) — you can opt out of marketing emails.',
        'Improve reliability, detect abuse, and protect platform security.',
        'Comply with applicable law and respond to lawful requests.',
        'Send product announcements and tips — you can unsubscribe from marketing emails at any time.',
      ],
      callout:
        'We do not use your resume content to train public third-party AI models without your consent. AI processing is scoped to delivering your requested outputs.',
    },
    {
      title: '5. Legal bases for processing (EEA / UK)',
      paragraphs: ['If you are in the EEA or UK, we process personal data under GDPR on these bases:'],
      list: [
        'Contract: to provide the service you signed up for.',
        'Legitimate interests: security, fraud prevention, and product improvement (balanced against your rights).',
        'Consent: where required — e.g. non-essential cookies or optional marketing.',
        'Legal obligation: tax, accounting, or regulatory requirements.',
      ],
    },
    {
      title: '6. How we share information',
      paragraphs: ['We do not sell your personal information. We share data only in these cases:'],
      list: [
        'Service providers who help us operate GoGrabJob (cloud hosting, AI inference, email, analytics, payments) under strict data-processing terms.',
        'When you explicitly choose to apply or share materials with an employer or third party.',
        'If required by law, court order, or to protect rights, safety, and integrity of our users.',
        'In a merger or acquisition, with notice where legally required.',
      ],
      subsections: [
        {
          title: 'Key subprocessors',
          list: [
            'Supabase / Firebase — authentication, database, and file storage.',
            'AI providers (e.g. OpenAI, Anthropic, Groq) — resume and JD processing, under our instructions.',
            'Cashfree / Razorpay — payment processing.',
          ],
        },
      ],
    },
    {
      title: '7. Data retention',
      paragraphs: [
        'We keep your account and resume data while your account is active. If you delete your account, we remove or anonymise personal data within 30 days, except where we must retain records for legal, tax, or dispute-resolution purposes.',
        'Backups may persist for up to 90 days before automatic purging.',
      ],
      list: [
        'Billing records: retained for 7 years where required by tax law.',
        'Support tickets: retained for 2 years unless you request earlier deletion.',
      ],
    },
    {
      title: '8. Security',
      paragraphs: [
        'We use encryption in transit (TLS) and at rest for stored files and database records. Access to production systems is restricted, logged, and role-based. No method of transmission over the internet is 100% secure; we continuously review our controls.',
      ],
      list: [
        'Incident response procedures with containment, investigation, and user notification where required.',
      ],
    },
    {
      title: '9. Your rights',
      paragraphs: ['Depending on your location, you may have the right to:'],
      list: [
        'Access and receive a copy of your personal data.',
        'Correct inaccurate information.',
        'Delete your account and associated data (Settings → Account).',
        'Export your data in a portable format (Settings → Privacy).',
        'Object to or restrict certain processing.',
        'Withdraw consent where processing is consent-based.',
        'Lodge a complaint with a supervisory authority (EEA/UK users).',
      ],
      callout: `Indian users: see our DPDP Compliance page. EEA/UK users: see GDPR Compliance. Contact ${CONTACT} for any request — we respond within 30 days.`,
    },
    {
      title: '10. Account deletion',
      paragraphs: [
        'You may delete your account from Settings → Account → Delete account. Deletion is irreversible after the confirmation period.',
      ],
      list: [
        'Personal profile and resume files are removed from active systems within 30 days.',
        'Backups are purged on a rolling schedule (up to 90 days).',
        'Anonymised usage statistics may be retained for analytics.',
      ],
    },
    {
      title: '11. International transfers',
      paragraphs: [
        'GoGrabJob is based in India. Some subprocessors may process data in other countries. Where required, we use appropriate safeguards such as standard contractual clauses or equivalent mechanisms.',
      ],
    },
    {
      title: '12. Children',
      paragraphs: [
        'GoGrabJob is not directed at children under 16. We do not knowingly collect data from children. Contact us if you believe a child has provided personal information.',
      ],
    },
    {
      title: '13. Changes to this policy',
      paragraphs: [
        'We may update this policy when our practices or legal requirements change. Material updates will be posted on this page with a revised "Last updated" date. Continued use after changes constitutes acceptance.',
      ],
    },
    {
      title: '14. Contact us',
      paragraphs: [
        `${COMPANY} · ${PRODUCT}`,
        `Email: ${CONTACT}`,
        'Address: Bengaluru, Karnataka, India',
      ],
    },
  ],
};

export const termsPolicy: PolicyDocument = {
  id: 'terms',
  title: 'Terms of Service',
  eyebrow: 'Legal · Terms',
  effectiveDate: EFFECTIVE,
  lastUpdated: UPDATED,
  summary:
    'These terms govern your use of GoGrabJob. By using our platform you agree to use it lawfully, keep your account secure, and understand that AI outputs are tools to assist your job search — not guarantees of employment.',
  sections: [
    {
      title: '1. Agreement',
      paragraphs: [
        `These Terms of Service ("Terms") are a binding agreement between you and ${COMPANY} ("we", "us") for use of ${PRODUCT} ("Service"), including our website, apps, and APIs.`,
        'If you use the Service on behalf of an organisation, you confirm you have authority to bind that organisation to these Terms.',
      ],
    },
    {
      title: '2. Eligibility',
      paragraphs: [
        'You must be at least 16 years old and able to form a binding contract. You may not use the Service if you are barred under applicable law or if we have suspended your account for violation of these Terms.',
      ],
    },
    {
      title: '3. Your account',
      list: [
        'Provide accurate registration information and keep it up to date.',
        'Keep your login credentials confidential; you are responsible for activity under your account.',
        'Notify us promptly at support@gograbjob.com if you suspect unauthorised access.',
        'One person per account unless we approve a team or enterprise arrangement separately.',
      ],
    },
    {
      title: '4. The Service',
      paragraphs: [
        'GoGrabJob provides job discovery and ranking, AI-assisted resume tailoring and ATS scoring, cover letter drafting, application tracking, and interview prep. You upload or paste your own resume — we align it to each role; we do not require you to build one from scratch. Features vary by plan (Free, Monthly, Quarterly, Half-Yearly).',
      ],
      callout:
        'AI-generated content is provided as a draft for your review. You are responsible for verifying accuracy before submitting applications. We do not guarantee interviews, offers, or ATS outcomes.',
    },
    {
      title: '5. Acceptable use',
      paragraphs: ['You agree not to:'],
      list: [
        'Upload unlawful, misleading, or third-party content without permission.',
        'Reverse-engineer, scrape, or overload our systems.',
        'Use the Service to spam employers or misrepresent qualifications.',
        'Share account access or resell Service access without authorisation.',
        'Circumvent usage limits, credits, or payment requirements.',
      ],
    },
    {
      title: '6. Subscriptions and payments',
      list: [
        'Paid plans are billed in INR (or your displayed local currency) and may auto-renew unless cancelled before the renewal date.',
        'Prices and plan features are shown on our Pricing page and may change with reasonable notice.',
        'Taxes may apply based on your location.',
        'Refunds are governed by our Refund & Cancellation Policy.',
      ],
    },
    {
      title: '7. Intellectual property',
      subsections: [
        {
          title: '7.1 Your content',
          paragraphs: [
            'You retain ownership of resumes and materials you upload. You grant us a limited licence to host, process, and display your content solely to operate and improve the Service for you.',
          ],
        },
        {
          title: '7.2 Our content',
          paragraphs: [
            'GoGrabJob branding, software, UI, and documentation are owned by Zykes Technologies LLP. You may not copy or redistribute them except as permitted by these Terms.',
          ],
        },
      ],
    },
    {
      title: '8. Privacy',
      paragraphs: [
        'Our Privacy Policy, Cookie Policy, GDPR Compliance, and DPDP Compliance pages describe how we handle personal data and are incorporated into these Terms by reference.',
      ],
    },
    {
      title: '9. Disclaimers',
      paragraphs: [
        'THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, EXPRESS OR IMPLIED, INCLUDING MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, AND NON-INFRINGEMENT.',
      ],
    },
    {
      title: '10. Limitation of liability',
      paragraphs: [
        `To the maximum extent permitted by law, ${COMPANY} shall not be liable for indirect, incidental, special, consequential, or punitive damages, or loss of profits, data, or goodwill. Our total liability for any claim relating to the Service is limited to the amount you paid us in the twelve (12) months before the claim, or ₹5,000, whichever is greater.`,
      ],
    },
    {
      title: '11. Termination',
      paragraphs: [
        'You may stop using the Service and delete your account at any time. We may suspend or terminate access if you breach these Terms, create risk for other users, or as required by law. Sections that by nature should survive (IP, disclaimers, liability limits) will survive termination.',
      ],
    },
    {
      title: '12. Modifications',
      paragraphs: [
        'We may update these Terms or the Service from time to time. Material changes will be posted on this page with an updated date. Continued use after changes constitutes acceptance. If you disagree, you should stop using the Service and delete your account.',
      ],
    },
    {
      title: '13. Dispute resolution',
      paragraphs: [
        'We prefer to resolve concerns informally — contact us first at support@gograbjob.com. If a dispute cannot be resolved, it will be subject to the governing law and jurisdiction in Section 14 below, unless mandatory consumer rights in your country require otherwise.',
      ],
    },
    {
      title: '14. Governing law',
      paragraphs: [
        'These Terms are governed by the laws of India. Courts in Bengaluru, Karnataka shall have exclusive jurisdiction, subject to mandatory consumer protection laws in your country of residence where applicable.',
      ],
    },
    {
      title: '15. Contact',
      paragraphs: [`Questions about these Terms: ${CONTACT}`],
    },
  ],
};

export const gdprPolicy: PolicyDocument = {
  id: 'gdpr',
  title: 'GDPR Compliance',
  eyebrow: 'Legal · GDPR',
  effectiveDate: EFFECTIVE,
  lastUpdated: UPDATED,
  summary:
    'This page explains how GoGrabJob meets GDPR requirements for users in the European Economic Area, United Kingdom, and Switzerland — and how to exercise your data protection rights.',
  sections: [
    {
      title: '1. Scope',
      paragraphs: [
        'The General Data Protection Regulation (GDPR) applies when we offer services to individuals in the EEA, UK, or Switzerland, or when we monitor their behaviour within those regions.',
        'GoGrabJob is the data controller for personal data processed through your account. Our Privacy Policy describes categories of data we collect and why.',
      ],
    },
    {
      title: '2. Your GDPR rights',
      cards: [
        {
          title: 'Right of access',
          body: 'Request a copy of personal data we hold about you. Use Settings → Export data or email support@gograbjob.com.',
        },
        {
          title: 'Right to rectification',
          body: 'Correct inaccurate profile or resume information directly in your account or via support.',
        },
        {
          title: 'Right to erasure',
          body: 'Delete your account from Settings. We will remove personal data unless retention is legally required.',
        },
        {
          title: 'Right to restrict processing',
          body: 'Ask us to limit how we use your data in certain circumstances (e.g. while a dispute is resolved).',
        },
        {
          title: 'Right to data portability',
          body: 'Receive your data in a structured, machine-readable format (JSON export available in Settings).',
        },
        {
          title: 'Right to object',
          body: 'Object to processing based on legitimate interests or direct marketing. We will assess and respond promptly.',
        },
      ],
      callout: 'We respond to verified requests within 30 days (extendable by 60 days for complex requests, with notice).',
    },
    {
      title: '3. Lawful bases',
      list: [
        'Performance of a contract — providing the Service you subscribed to.',
        'Legitimate interests — security, analytics, and product improvement, where not overridden by your rights.',
        'Consent — non-essential cookies and optional communications.',
        'Legal obligation — compliance with tax, accounting, or regulatory duties.',
      ],
    },
    {
      title: '4. International transfers',
      paragraphs: [
        'Data may be processed in India and in countries where our subprocessors operate. Where GDPR requires safeguards for transfers outside the EEA/UK, we rely on adequacy decisions, Standard Contractual Clauses (SCCs), or equivalent mechanisms.',
      ],
    },
    {
      title: '5. Data Protection Officer',
      paragraphs: [
        'For GDPR enquiries or to exercise your rights:',
        `Data Protection contact: ${CONTACT}`,
        `${COMPANY}, Bengaluru, India`,
      ],
    },
    {
      title: '6. Supervisory authority',
      paragraphs: [
        'You have the right to lodge a complaint with your local data protection authority if you believe our processing violates GDPR. We encourage you to contact us first so we can address your concern.',
      ],
    },
    {
      title: '8. Automated decision-making',
      paragraphs: [
        'GoGrabJob uses AI to suggest resume edits and scores. These outputs assist your decisions — they do not automatically apply to jobs or reject candidates. You review and approve all content before use.',
      ],
    },
    {
      title: '9. Records and accountability',
      list: [
        'We maintain records of processing activities as required by GDPR.',
        'Data processing agreements are in place with subprocessors.',
        'We conduct periodic privacy reviews when we launch new features.',
      ],
    },
    {
      title: '10. Data breach notification',
      paragraphs: [
        'If a personal data breach is likely to pose a risk to your rights, we will notify the relevant supervisory authority within 72 hours where required and inform affected users without undue delay when the risk is high.',
      ],
    },
  ],
};

export const dpdpPolicy: PolicyDocument = {
  id: 'dpdp',
  title: 'DPDP Compliance',
  eyebrow: 'Legal · India DPDP Act',
  effectiveDate: EFFECTIVE,
  lastUpdated: UPDATED,
  summary:
    'GoGrabJob complies with India\'s Digital Personal Data Protection Act, 2023 (DPDP Act). This page explains how we handle personal data of users in India and how you can exercise your rights as a Data Principal.',
  sections: [
    {
      title: '1. Applicability',
      paragraphs: [
        'The DPDP Act governs processing of digital personal data in India. GoGrabJob, operated by Zykes Technologies LLP, acts as a Data Fiduciary when we determine the purpose and means of processing your personal data through our platform.',
        'This notice supplements our Privacy Policy with India-specific information.',
      ],
    },
    {
      title: '2. Personal data we process',
      list: [
        'Identity and contact data (name, email, phone).',
        'Professional and employment-related data (resume, work history, skills, applications).',
        'Usage, device, and technical data necessary to operate the Service.',
        'Payment and subscription metadata (processed via licensed payment aggregators).',
      ],
      callout: 'We process personal data only for lawful purposes connected to providing and improving GoGrabJob.',
    },
    {
      title: '3. Consent and notice',
      paragraphs: [
        'We provide clear notice at sign-up and in this policy about what data we collect and why. Where the DPDP Act requires consent — for example, certain optional marketing or non-essential cookies — we obtain it separately and allow withdrawal as easily as giving consent.',
        'For core service delivery (account, resume optimization, billing), processing is necessary to perform our contract with you.',
      ],
    },
    {
      title: '4. Rights of Data Principals',
      list: [
        'Right to access information about personal data we process about you.',
        'Right to correction and erasure of inaccurate or outdated data.',
        'Right to grievance redressal through our designated contact.',
        'Right to nominate another individual to exercise your rights in the event of death or incapacity.',
        'Right to withdraw consent where processing is consent-based, without affecting prior lawful processing.',
      ],
      paragraphs: [`Exercise your rights via Settings in your account or email ${CONTACT}. We aim to respond within 30 days.`],
    },
    {
      title: '5. Children\'s data',
      paragraphs: [
        'We do not knowingly process personal data of children under 18 without verifiable parental consent as required by law. GoGrabJob is intended for job seekers who can enter a binding agreement.',
      ],
    },
    {
      title: '6. Data security and breach',
      paragraphs: [
        'We implement reasonable security safeguards — encryption, access controls, and monitoring — appropriate to the nature of resume and account data we hold.',
        'If a personal data breach affects you, we will notify the Data Protection Board of India and affected Data Principals as required by law.',
      ],
    },
    {
      title: '7. Cross-border transfers',
      paragraphs: [
        'Some subprocessors may process data outside India. Where the DPDP Act restricts transfers, we comply with notified conditions and contractual safeguards.',
      ],
    },
    {
      title: '8. Grievance redressal',
      paragraphs: [
        `Grievance Officer: ${CONTACT}`,
        'We will acknowledge grievances within 24 hours and aim to resolve them within 30 days. If unresolved, you may approach the Data Protection Board of India under the DPDP Act.',
      ],
    },
    {
      title: '10. Retention schedule (India)',
      list: [
        'Account and resume data: while your account is active, plus up to 30 days after deletion.',
        'Payment records: as required by Indian tax and commercial law.',
        'Consent logs (cookies): up to 1 year.',
      ],
    },
    {
      title: '11. Updates',
      paragraphs: [
        'We will update this page when our practices or DPDP rules change. Material changes will be communicated through the platform or email where appropriate.',
      ],
    },
  ],
};

export const refundPolicy: PolicyDocument = {
  id: 'refund',
  title: 'Refund & Cancellation Policy',
  eyebrow: 'Legal · Billing',
  effectiveDate: EFFECTIVE,
  lastUpdated: UPDATED,
  summary:
    'Start free, upgrade when ready, and cancel anytime. Paid subscriptions auto-renew but you keep access until the current period ends. Refunds are limited — details below.',
  sections: [
    {
      title: '1. Plans overview',
      paragraphs: ['GoGrabJob offers:'],
      list: [
        'Free — 3 lifetime optimizations, no payment required.',
        'Monthly — ₹349/month, 60 optimizations per month, auto-renew.',
        'Quarterly — ₹749 per 3 months, 180 optimizations total, auto-renew.',
        'Half-Yearly — ₹1,499 per 6 months, 360 optimizations total, auto-renew.',
      ],
    },
    {
      title: '2. Cancellation',
      paragraphs: ['You may cancel a paid subscription at any time:'],
      list: [
        'In-app: Settings → Billing → Cancel subscription.',
        `By email: ${CONTACT} with your account email (processed within 1 business day).`,
      ],
      callout:
        'Cancellation stops future charges. You retain paid features until the end of the current billing period. After that, your account moves to the Free plan — your data remains accessible.',
    },
    {
      title: '3. Refund policy',
      paragraphs: [
        'Credit packs and subscription payments are generally non-refundable once the billing period has started and optimizations have been made available.',
        'We may issue a full or partial refund at our discretion for:',
      ],
      list: [
        'Duplicate or erroneous charges verified by our payment partner.',
        'Technical failures that prevented delivery of paid features for an extended period.',
        'Mandatory refunds required under applicable consumer protection law.',
      ],
      callout:
        'Refund requests must be submitted within 7 days of the charge to support@gograbjob.com with your transaction ID. Approved refunds are returned to the original payment method within 7–14 business days.',
    },
    {
      title: '4. Free trial and promotions',
      paragraphs: [
        'If we offer a promotional trial or discount, specific terms will be shown at checkout. Promotional credits expire as stated in the offer and are not convertible to cash.',
      ],
    },
    {
      title: '5. Chargebacks',
      paragraphs: [
        'Please contact us before initiating a chargeback so we can investigate. Unfounded chargebacks may result in account suspension pending resolution.',
      ],
    },
    {
      title: '7. Plan changes and failed payments',
      paragraphs: [
        'If a renewal payment fails, we may retry billing and notify you by email. After repeated failures, your account may downgrade to Free until payment succeeds.',
        'Upgrading mid-cycle may prorate or start a new billing period as shown at checkout. Downgrades take effect at the next renewal date.',
      ],
    },
    {
      title: '8. Contact',
      paragraphs: [`Billing questions: ${CONTACT}`],
    },
  ],
};

export const trustPolicy: PolicyDocument = {
  id: 'trust',
  title: 'Trust Center',
  eyebrow: 'Security & Trust',
  effectiveDate: EFFECTIVE,
  lastUpdated: UPDATED,
  summary:
    'Transparency about how GoGrabJob protects your resume data, which partners we rely on, and the practices we follow to keep the platform secure.',
  sections: [
    {
      title: '1. Our commitment',
      paragraphs: [
        'Your resume and job-search data are sensitive. We design GoGrabJob with privacy and security as core requirements — not afterthoughts. This Trust Center summarises our controls; detailed legal terms are in our Privacy Policy and related documents.',
      ],
    },
    {
      title: '2. Security practices',
      cards: [
        {
          title: 'Encryption',
          body: 'TLS 1.2+ for data in transit. Encrypted storage for resumes and account data at rest.',
        },
        {
          title: 'Access control',
          body: 'Role-based access to production systems. Multi-factor authentication for internal admin access.',
        },
        {
          title: 'Monitoring',
          body: 'Logging, anomaly detection, and regular review of access patterns.',
        },
        {
          title: 'Secure development',
          body: 'Code review, dependency scanning, and environment separation (dev / staging / production).',
        },
      ],
    },
    {
      title: '3. Infrastructure partners',
      table: {
        headers: ['Service', 'Provider', 'Purpose'],
        rows: [
          ['Database & auth', 'Supabase', 'Account data, application storage'],
          ['Hosting & CDN', 'Netlify / cloud edge', 'Website delivery'],
          ['AI inference', 'OpenAI, Anthropic, Groq', 'Resume & JD processing'],
          ['Payments', 'Cashfree, Razorpay', 'Subscription billing'],
          ['Email', 'Transactional email provider', 'Account & billing notices'],
        ],
      },
      callout: 'Subprocessors are bound by data-processing agreements and may only use data to provide services to GoGrabJob.',
    },
    {
      title: '4. Data handling principles',
      list: [
        'Data minimisation — we collect what the product needs, not more.',
        'Purpose limitation — resume content is used to deliver your requested outputs.',
        'No sale of personal data to recruiters or data brokers.',
        'User control — export and delete from account Settings.',
        'Incident response — documented process for detection, containment, and notification.',
      ],
    },
    {
      title: '5. Compliance alignment',
      paragraphs: [
        'We align our privacy programme with GDPR (EEA/UK), India\'s DPDP Act 2023, and applicable consumer protection rules. We do not claim certifications we have not earned; when we complete formal audits (e.g. SOC 2), we will publish them here.',
      ],
    },
    {
      title: '6. Responsible AI',
      list: [
        'AI outputs are generated for your review — not auto-submitted to employers.',
        'We scope model usage to your session and service delivery.',
        'We monitor for abuse and harmful use of the platform.',
      ],
    },
    {
      title: '8. Incident response',
      list: [
        'Detection via monitoring and user reports.',
        'Containment and root-cause analysis by our engineering team.',
        'Notification to users and regulators when legally required.',
        'Post-incident review and remediation.',
      ],
    },
    {
      title: '9. Report a concern',
      paragraphs: [
        `Security vulnerability or data concern: ${CONTACT}`,
        'We appreciate responsible disclosure and will investigate good-faith reports promptly.',
      ],
    },
  ],
};
